import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  fmtClock,
  fmtDate,
  fmtDateShort,
  fmtTime,
  fmtWeekday,
} from "@/lib/tz";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mm = minutes % 60;
    return `${hours}:${String(mm).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function asDate(date: Date | string): Date {
  return typeof date === "string" ? new Date(date) : date;
}

export function formatTimeAgo(date: Date | string, now: Date = new Date()): string {
  const d = asDate(date);
  const diffMs = now.getTime() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return fmtDate(d) ?? "";
}

// Igual de compacto que formatTimeAgo pero con el "hace" puesto ("hace 5m",
// "hace 3h") para donde el número pelado se lee ambiguo (el timestamp de las
// cards). Conserva "ahora" y el fallback a fecha absoluta (>7 días) sin
// prefijo — "hace 17/06" no es castellano.
export function formatTimeAgoHace(date: Date | string, now: Date = new Date()): string {
  const ago = formatTimeAgo(date, now);
  if (ago === "ahora" || ago === "" || ago.includes("/")) return ago;
  return `hace ${ago}`;
}

// Relativo detallado con dos unidades: "hace 3h 40m", "hace 2d 5h", "hace 12m".
// A diferencia de formatTimeAgo (una sola unidad, compacto), incluye el prefijo
// "hace" y la unidad inferior para más precisión. Para fechas > 7 días devuelve
// la fecha absoluta (sin prefijo).
export function formatTimeAgoLong(date: Date | string, now: Date = new Date()): string {
  const d = asDate(date);
  const diffMs = now.getTime() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min}m`;
  const h = Math.floor(min / 60);
  const remMin = min % 60;
  if (h < 24) return remMin > 0 ? `hace ${h}h ${remMin}m` : `hace ${h}h`;
  const days = Math.floor(h / 24);
  const remH = h % 24;
  if (days < 7) return remH > 0 ? `hace ${days}d ${remH}h` : `hace ${days}d`;
  return fmtDate(d) ?? "";
}

// Los formatters absolutos delegan en tz.ts (TZ explícita = la del tenant en
// cliente). Antes usaban date-fns `format()`, que tomaba la zona de la máquina
// donde corría el código — fuente de horarios corridos. Se mantienen los nombres
// y los formatos visuales para no tocar los callsites.
export function formatTime(date: Date | string): string {
  return fmtTime(asDate(date)) ?? "";
}

export function formatDateTime(date: Date | string): string {
  // Formato histórico "dd/MM HH:mm" (mes con cero, separado por espacio) — lo
  // armamos con los helpers tz-safe para preservarlo exacto en los callsites.
  const d = asDate(date);
  const date5 = fmtDate(d)?.slice(0, 5); // "dd/MM" de "dd/MM/yyyy"
  const time = fmtTime(d);
  return date5 && time ? `${date5} ${time}` : "";
}

export function formatDateLong(date: Date | string): string {
  return fmtDateShort(asDate(date)) ?? "";
}

// Día de la semana en castellano, capitalizado: "Martes", "Lunes".
export function formatWeekday(date: Date | string): string {
  return fmtWeekday(asDate(date)) ?? "";
}

// Fecha corta numérica: "11/02/2026".
export function formatShortDate(date: Date | string): string {
  return fmtDate(asDate(date)) ?? "";
}

export function formatClockTime(date: Date | string): string {
  return fmtClock(asDate(date)) ?? "";
}

export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return phone;
  return `***${digits.slice(-4)}`;
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
